import type { CreditWindowUsage } from '@xinfer/api-client';
import { Badge, Card, cn } from '@xinfer/ui';
import {
  explainCredits,
  formatCredits,
  formatTimestamp,
  windowFraction,
  windowLabel,
  windowTone,
} from '../lib/format';

const trackTone = {
  neutral: 'bg-primary',
  warning: 'bg-warning',
  danger: 'bg-destructive',
} as const;

/**
 * One rolling window, as a bar with its headroom spelled out.
 *
 * A bar rather than a number because the question a user actually has is "how
 * much room is left", and a pair of raw totals makes them do the subtraction.
 * The remaining figure is stated anyway, because a bar cannot be read to a
 * credit.
 *
 * An uncapped window renders as a plain total with no track. Drawing an empty
 * bar for it would imply a limit that does not exist — which is precisely the
 * wrong thing to tell someone on the plan that predates credits.
 */
export function CreditWindowBar({ usage }: { usage: CreditWindowUsage }) {
  const fraction = windowFraction(usage.used_milli, usage.cap_milli);
  const tone = windowTone(fraction);
  const remaining =
    usage.cap_milli === null ? null : Math.max(0, usage.cap_milli - usage.used_milli);

  return (
    <Card className="p-4">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Last {windowLabel(usage.window)}
        </span>
        {fraction !== null && (
          <Badge variant={tone === 'neutral' ? 'outline' : tone}>
            {Math.round(fraction * 100)}% used
          </Badge>
        )}
      </div>

      <div className="mt-2 flex items-baseline gap-1.5">
        <span className={cn('text-2xl font-semibold tabular-nums', tone !== 'neutral' && 'text-destructive')}>
          {formatCredits(usage.used_milli)}
        </span>
        <span className="text-sm text-muted-foreground">
          {usage.cap_milli === null ? 'credits' : `of ${formatCredits(usage.cap_milli)} credits`}
        </span>
      </div>

      {fraction === null ? (
        <p className="mt-3 text-sm text-muted-foreground">
          No limit on this window for this plan.
        </p>
      ) : (
        <>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={cn('h-full rounded-full transition-all', trackTone[tone])}
              style={{ width: `${fraction * 100}%` }}
            />
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            {remaining === 0 ? (
              <>
                Exhausted. Requests are refused with{' '}
                <code className="font-mono">429 credits_exhausted</code> until this window rolls
                {usage.frees_at ? ` — from ${formatTimestamp(usage.frees_at)}` : ''}.
              </>
            ) : (
              <>
                {formatCredits(remaining ?? 0)} credits left — {explainCredits(remaining ?? 0)}.
              </>
            )}
          </p>
        </>
      )}
    </Card>
  );
}
